import JSZip from 'jszip';
import {
  replaceDocumentBytes,
  readChangeTrackingMode,
  setChangeTrackingMode,
} from './documentIO';
import { hashString } from '../lib/utils';

/**
 * Surgical OOXML-level diffing for Word documents.
 *
 * The AI edit agent returns a DOCX with `w:ins`/`w:del` revision markup
 * embedded (via docx-revisions). The naive way to apply it is
 * `insertFileFromBase64(replace)` — works, but destroys Word's undo history
 * and makes the whole document flicker.
 *
 * Instead, we:
 *   1. Unzip both the current and edited DOCX
 *   2. Extract each <w:p> (paragraph) element from word/document.xml
 *   3. Align paragraphs between the two documents (by content hash)
 *   4. For each non-matching paragraph, apply a surgical insertOoxml() on
 *      the corresponding Word.Range.
 *
 * Guardrails:
 *   - If the edited doc has very different paragraph count → fall back
 *   - If >50% of paragraphs changed → fall back (full replace is cheaper)
 *   - If insertOoxml throws for any range → fall back
 *
 * Both paths run with change tracking turned OFF (see applyEditedDocument): the
 * server DOCX already carries `w:ins`/`w:del` revisions, so we must NOT let Word
 * add its own tracking on top — doing so re-wraps the whole insert as one fresh
 * change (blue) and drops the embedded deletions. The user's original tracking
 * mode is captured and restored around the apply.
 */

export interface ApplyResult {
  method: 'surgical' | 'fallback';
  paragraphsChanged?: number;
  reason?: string;
}

interface DiffOp {
  /** Index in the CURRENT document's paragraph array */
  currentIndex: number;
  operation: 'replace' | 'insert-after' | 'delete';
  /** Raw OOXML to insert (for replace/insert-after ops) */
  newOoxml?: string;
}

const MAX_CHANGE_RATIO = 0.5;

export async function applyEditedDocument(
  editedDocxBytes: Uint8Array,
  currentDocxBytes?: Uint8Array,
): Promise<ApplyResult> {
  // The server DOCX already carries w:ins/w:del revisions authored
  // "NyayAssist AI". Force change tracking OFF for the whole apply so Word
  // preserves that embedded redline verbatim — inserting it while tracking is
  // ON (e.g. the user is in Reviewing mode) makes Word re-wrap the entire
  // inserted content as a fresh insertion by the current user, turning the
  // document blue AND dropping the embedded deletions. Capture the user's mode
  // first and restore it in `finally` so their Track Changes preference (and
  // any new revisions) is untouched.
  const priorMode = await readChangeTrackingMode();
  await setChangeTrackingMode(Word.ChangeTrackingMode.off);
  try {
    // First attempt: surgical diff, only if we have both sides
    if (currentDocxBytes) {
      try {
        const result = await applyOoxmlDiff(currentDocxBytes, editedDocxBytes);
        if (result.success) {
          return { method: 'surgical', paragraphsChanged: result.paragraphsChanged };
        }
        // Surgical path declined (too many changes) — fall through to replace
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[OOXML] Surgical diff failed, falling back to full replace', err);
      }
    }

    await replaceDocumentBytes(editedDocxBytes.buffer as ArrayBuffer);
    return { method: 'fallback' };
  } finally {
    await setChangeTrackingMode(priorMode);
  }
}

/**
 * Attempt surgical diff + apply. Throws on unrecoverable errors (propagates
 * to applyEditedDocument which falls back). Returns { success: false } for
 * "declined but no error" cases like "too many changes".
 */
async function applyOoxmlDiff(
  current: Uint8Array,
  edited: Uint8Array,
): Promise<{ success: boolean; paragraphsChanged?: number; reason?: string }> {
  const currentXml = await extractDocumentXml(current);
  const editedXml = await extractDocumentXml(edited);

  const currentParas = extractParagraphs(currentXml);
  const editedParas = extractParagraphs(editedXml);

  if (currentParas.length === 0 || editedParas.length === 0) {
    return { success: false, reason: 'empty_document_body' };
  }

  const diff = diffParagraphs(currentParas, editedParas);

  if (diff.length === 0) {
    return { success: true, paragraphsChanged: 0 };
  }
  if (diff.length / currentParas.length > MAX_CHANGE_RATIO) {
    return { success: false, reason: 'too_many_changes' };
  }

  // Apply in reverse order so index shifts from insert/delete don't cascade.
  // Change tracking is already OFF (set by applyEditedDocument) so the server's
  // embedded w:ins/w:del in each replaced paragraph survive as authored.
  await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load('items');
    await context.sync();

    if (paragraphs.items.length !== currentParas.length) {
      throw new Error(
        `paragraph_count_mismatch: word reports ${paragraphs.items.length}, doc has ${currentParas.length}`,
      );
    }

    for (let i = diff.length - 1; i >= 0; i--) {
      const op = diff[i];
      const targetPara = paragraphs.items[op.currentIndex];
      if (!targetPara) {
        throw new Error(`paragraph_index_out_of_range: ${op.currentIndex}`);
      }
      const range = targetPara.getRange();
      if (op.operation === 'replace' && op.newOoxml) {
        range.insertOoxml(wrapInDocument(op.newOoxml), Word.InsertLocation.replace);
      } else if (op.operation === 'insert-after' && op.newOoxml) {
        range.insertOoxml(wrapInDocument(op.newOoxml), Word.InsertLocation.after);
      } else if (op.operation === 'delete') {
        range.delete();
      }
    }
    await context.sync();
  });

  return { success: true, paragraphsChanged: diff.length };
}

async function extractDocumentXml(docxBytes: Uint8Array): Promise<string> {
  const zip = await JSZip.loadAsync(docxBytes);
  const entry = zip.file('word/document.xml');
  if (!entry) throw new Error('Invalid DOCX: missing word/document.xml');
  return entry.async('string');
}

/**
 * Extract top-level <w:p> elements from a document.xml body.
 * Returns the full XML of each paragraph so we can re-insert it verbatim.
 *
 * We don't use a DOM parser here because:
 *   1. Browsers' DOMParser mangles namespaces on serialize
 *   2. We want byte-identical roundtrips for `w:ins`/`w:del` children
 *
 * Instead we do a lightweight tag-balanced scan that finds matching
 * <w:p ...>…</w:p> ranges. This is robust for well-formed DOCX content,
 * which Office produces.
 */
function extractParagraphs(xml: string): string[] {
  const results: string[] = [];
  const re = /<w:p(?:\s[^>]*)?>/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(xml)) !== null) {
    const start = match.index;
    const selfClose = match[0].endsWith('/>');
    if (selfClose) {
      results.push(match[0]);
      continue;
    }
    // Scan forward for the matching </w:p>, allowing nested w:p (rare but possible in fields)
    let depth = 1;
    let cursor = re.lastIndex;
    while (depth > 0 && cursor < xml.length) {
      const nextOpen = xml.indexOf('<w:p', cursor);
      const nextClose = xml.indexOf('</w:p>', cursor);
      if (nextClose === -1) break;
      if (nextOpen !== -1 && nextOpen < nextClose) {
        // Only count as a nested paragraph if it's actually <w:p> or <w:p ...>
        const charAfter = xml[nextOpen + 4];
        if (charAfter === ' ' || charAfter === '>') {
          depth++;
          cursor = nextOpen + 4;
          continue;
        }
        cursor = nextOpen + 4;
        continue;
      }
      depth--;
      cursor = nextClose + '</w:p>'.length;
      if (depth === 0) {
        results.push(xml.slice(start, cursor));
        re.lastIndex = cursor;
        break;
      }
    }
  }
  return results;
}

/**
 * Simple paragraph-level diff using longest-common-subsequence on content hashes.
 * Returns ops relative to the CURRENT document's indices.
 */
function diffParagraphs(current: string[], edited: string[]): DiffOp[] {
  // Normalize paragraphs by stripping w:ins/w:del markup so we match on the
  // original content, not revision decorations.
  const currentHashes = current.map((p) => hashString(stripRevisionMarkup(p)));
  const editedHashes = edited.map((p) => hashString(stripRevisionMarkup(p)));

  // LCS DP table
  const m = currentHashes.length;
  const n = editedHashes.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (currentHashes[i] === editedHashes[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (currentHashes[i] === editedHashes[j]) {
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      // Current paragraph has no match in edited → delete
      ops.push({ currentIndex: i, operation: 'delete' });
      i++;
    } else {
      // Edited paragraph has no match in current → insert after previous current paragraph
      ops.push({
        currentIndex: Math.max(0, i - 1),
        operation: 'insert-after',
        newOoxml: edited[j],
      });
      j++;
    }
  }
  while (i < m) {
    ops.push({ currentIndex: i, operation: 'delete' });
    i++;
  }
  while (j < n) {
    ops.push({
      currentIndex: Math.max(0, m - 1),
      operation: 'insert-after',
      newOoxml: edited[j],
    });
    j++;
  }

  // Coalesce adjacent delete+insert-after on the same index into a "replace"
  // so Word sees a single tracked change instead of a pair.
  const coalesced: DiffOp[] = [];
  for (let k = 0; k < ops.length; k++) {
    const curr = ops[k];
    const next = ops[k + 1];
    if (
      curr &&
      next &&
      curr.operation === 'delete' &&
      next.operation === 'insert-after' &&
      curr.currentIndex === next.currentIndex
    ) {
      coalesced.push({
        currentIndex: curr.currentIndex,
        operation: 'replace',
        newOoxml: next.newOoxml,
      });
      k++; // skip the next — we consumed it
      continue;
    }
    coalesced.push(curr);
  }
  return coalesced;
}

/**
 * Remove `w:ins` and `w:del` tracked-change wrappers so we can compare
 * paragraphs by their "original" content. The markup we keep in the OOXML
 * string for insertion — only the hash used for LCS matching is stripped.
 */
function stripRevisionMarkup(paragraphXml: string): string {
  return paragraphXml
    .replace(/<w:ins[^>]*>[\s\S]*?<\/w:ins>/g, '')
    .replace(/<w:del[^>]*>[\s\S]*?<\/w:del>/g, '')
    .replace(/<w:rPr>[\s\S]*?<\/w:rPr>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * `insertOoxml()` requires a full Word document XML, not a raw paragraph.
 * Wrap the paragraph fragment in the minimal envelope Word accepts.
 *
 * The `<w:document>` element MUST declare every namespace prefix the extracted
 * paragraph can reference, or the OOXML is not well-formed and insertOoxml
 * throws (forcing the wholesale fallback). Real Word paragraphs carry
 * `w14:paraId`/`w14:textId` (and may use w15/w16/mc/r), so we declare the full
 * common Word set — `xmlns:w` alone was the bug that made every edit fall back.
 */
function wrapInDocument(paragraphXml: string): string {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">',
    '  <pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml" pkg:padding="512">',
    '    <pkg:xmlData>',
    '      <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '        <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>',
    '      </Relationships>',
    '    </pkg:xmlData>',
    '  </pkg:part>',
    '  <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">',
    '    <pkg:xmlData>',
    '      <w:document' +
      ' xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"' +
      ' xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml"' +
      ' xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml"' +
      ' xmlns:w16se="http://schemas.microsoft.com/office/word/2015/wordml/symex"' +
      ' xmlns:w16cid="http://schemas.microsoft.com/office/word/2016/wordml/cid"' +
      ' xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"' +
      ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    '        <w:body>',
    `          ${paragraphXml}`,
    '        </w:body>',
    '      </w:document>',
    '    </pkg:xmlData>',
    '  </pkg:part>',
    '</pkg:package>',
  ].join('\n');
}
